import { Box, useColorModeValue } from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { colors } from '../colors';

export type SplitDirection = 'horizontal' | 'vertical';

export interface SplitPaneProps {
  direction?: SplitDirection;
  defaultSize?: number; // percentage for the start pane
  min?: number; // min percentage
  max?: number; // max percentage
  height?: number | string; // container height
  start: React.ReactNode; // left/top content
  end: React.ReactNode; // right/bottom content
  onChange?: (sizePercent: number) => void; // called when size changes
}

// A simple, accessible split pane with drag + keyboard resize.
export const SplitPane: React.FC<SplitPaneProps> = ({
  direction = 'horizontal',
  defaultSize = 70,
  min = 20,
  max = 80,
  height = 600,
  start,
  end,
  onChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<number>(defaultSize);
  const [isDragging, setIsDragging] = useState(false);
  const splitterBg = useColorModeValue(colors.gray[300], colors.gray[600]);
  const splitterHoverBg = useColorModeValue(colors.gray[400], colors.gray[500]);
  const borderColor = useColorModeValue(colors.gray[700], colors.gray[300]); // dark border in light mode, light border in dark mode

  useEffect(() => {
    setSize(defaultSize);
  }, [defaultSize, direction]);

  const clamp = useCallback(
    (val: number) => Math.max(min, Math.min(max, val)),
    [min, max],
  );

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      if (direction === 'horizontal') {
        const percentage = ((e.clientX - rect.left) / rect.width) * 100;
        const next = clamp(percentage);
        setSize(next);
        onChange?.(next);
      } else {
        const percentage = ((e.clientY - rect.top) / rect.height) * 100;
        const next = clamp(percentage);
        setSize(next);
        onChange?.(next);
      }
    },
    [clamp, direction, isDragging, onChange],
  );

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <Box
      ref={containerRef}
      width="100%"
      height={height}
      border={`1px solid ${borderColor}`}
      display="flex"
      resize="both"
      overflow="hidden"
      flexDirection={direction === 'horizontal' ? 'row' : 'column'}
      position="relative"
    >
      {/* Start Pane */}
      <Box
        width={direction === 'horizontal' ? `${size}%` : '100%'}
        height={direction === 'horizontal' ? '100%' : `${size}%`}
        minWidth={direction === 'horizontal' ? '200px' : 'auto'}
        minHeight={direction === 'vertical' ? '200px' : 'auto'}
      >
        {start}
      </Box>

      {/* Resize Handle */}
      <Box
        width={direction === 'horizontal' ? '4px' : '100%'}
        height={direction === 'horizontal' ? '100%' : '4px'}
        bg={splitterBg}
        cursor={direction === 'horizontal' ? 'col-resize' : 'row-resize'}
        _hover={{ bg: splitterHoverBg }}
        onMouseDown={handleMouseDown}
        position="relative"
        zIndex={1}
        role="separator"
        aria-orientation={
          direction === 'horizontal' ? 'vertical' : 'horizontal'
        }
        aria-label="Resize"
        tabIndex={0}
        onKeyDown={(e) => {
          const step = 2;
          if (direction === 'horizontal') {
            if (e.key === 'ArrowLeft') {
              const next = clamp(size - step);
              setSize(next);
              onChange?.(next);
            }
            if (e.key === 'ArrowRight') {
              const next = clamp(size + step);
              setSize(next);
              onChange?.(next);
            }
          } else {
            if (e.key === 'ArrowUp') {
              const next = clamp(size - step);
              setSize(next);
              onChange?.(next);
            }
            if (e.key === 'ArrowDown') {
              const next = clamp(size + step);
              setSize(next);
              onChange?.(next);
            }
          }
        }}
      />

      {/* End Pane */}
      <Box
        width={direction === 'horizontal' ? `${100 - size}%` : '100%'}
        height={direction === 'horizontal' ? '100%' : `${100 - size}%`}
        minWidth={direction === 'horizontal' ? '200px' : 'auto'}
        minHeight={direction === 'vertical' ? '200px' : 'auto'}
      >
        {end}
      </Box>
    </Box>
  );
};

export default SplitPane;
